<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useQuery } from '@tanstack/vue-query';
import axiosInstance from '@/services/interceptor.services';
import { ManageOrganisationService } from '@/services/manageOrganisation.services';
import type {
  IRestaurant,
  IApiResponseRestaurant,
} from '@/types/commerce.types';
import type { IManageOrganisation } from '@/types/manageOrgCommerce.types';

type ViewMode = 'table' | 'card';
type ApiSortBy = 'created' | 'name';
type ApiOrder = 'ASC' | 'DESC';

const router = useRouter();
const route = useRoute();
const { t } = useI18n({ useScope: 'global' });

// Onglet à ouvrir sur la fiche commerce quand on vient d'un lien "Configuration"
// de la sidebar (ex: Modules/Médias/Utilisateurs) — voir AppSidebar.vue.
const pendingTab = computed(() =>
  typeof route.query.pendingTab === 'string' ? route.query.pendingTab : '',
);
function openStore(id: string) {
  router.push(
    pendingTab.value
      ? `/commerces/${id}?tab=${pendingTab.value}`
      : `/commerces/${id}`,
  );
}

// ── Filtres API ──────────────────────────────────────────────────────────────
const search = ref(''); // désignation (client-side pour la frappe)
const designationQ = ref(''); // envoyé à l'API (debounce)
const orgIdFilter = ref(
  typeof route.query.organizationId === 'string'
    ? route.query.organizationId
    : '',
); // organizationId
const storeIdFilter = ref(
  typeof route.query.storeId === 'string' ? route.query.storeId : '',
); // id du commerce précis
const unaffiliated = ref(false); // uniquement non affiliés
const sortBy = ref<ApiSortBy>('name');
const order = ref<ApiOrder>('ASC');
const page = ref(1);
const PAGE_SIZE = 20;
const orgDropOpen = ref(false);
const orgSearch = ref('');
const storeDropOpen = ref(false);
const storeSearch = ref('');

watch(orgIdFilter, () => {
  storeIdFilter.value = '';
  storeSearch.value = '';
});

// Statut local (filtrage client-side sur isActive car l'API ne le supporte pas)
const statusFilter = ref<'all' | 'active' | 'inactive'>('active');
const STATUS_OPTS = computed(() => [
  { k: 'active' as const, l: t('commerces.statusActifs') },
  { k: 'inactive' as const, l: t('commerces.statusInactifs') },
  { k: 'all' as const, l: t('commerces.statusAll') },
]);

// Debounce désignation → reset page
let debTimer: ReturnType<typeof setTimeout>;
watch(search, (v) => {
  clearTimeout(debTimer);
  debTimer = setTimeout(() => {
    designationQ.value = v.trim();
    page.value = 1;
  }, 350);
});
watch(
  [orgIdFilter, storeIdFilter, unaffiliated, sortBy, order, statusFilter],
  () => {
    page.value = 1;
  },
);

// ── Query commerces ──────────────────────────────────────────────────────────
// Pas de paramètre serveur pour filtrer par un commerce précis (restaurantId
// n'existe que sur l'endpoint de recherche d'utilisateurs) : quand storeIdFilter
// est actif, on élargit la pageSize et on filtre côté client sur son id.
const queryKey = computed(() => [
  'commerces-page',
  page.value,
  designationQ.value,
  orgIdFilter.value,
  storeIdFilter.value,
  unaffiliated.value,
  sortBy.value,
  order.value,
]);

const {
  data: apiResp,
  isPending,
  isError,
} = useQuery({
  queryKey,
  queryFn: async () => {
    const params = new URLSearchParams({
      pagination: 'true',
      page: String(storeIdFilter.value ? 1 : page.value),
      pageSize: String(storeIdFilter.value ? 500 : PAGE_SIZE),
      sortBy: sortBy.value,
      order: order.value,
    });
    if (designationQ.value) params.append('designation', designationQ.value);
    if (orgIdFilter.value) params.append('organizationId', orgIdFilter.value);
    if (unaffiliated.value) params.append('unaffiliated', 'true');
    const r = await axiosInstance.get<IApiResponseRestaurant>(
      `restaurants/search?${params}`,
    );
    return r.data;
  },
  staleTime: 30_000,
  placeholderData: (prev: any) => prev,
});

const rawStores = computed<IRestaurant[]>(() => {
  const list = apiResp.value?.data ?? [];
  return storeIdFilter.value
    ? list.filter((s) => s.id === storeIdFilter.value)
    : list;
});
const totalItems = computed<number>(() =>
  storeIdFilter.value ? rawStores.value.length : (apiResp.value?.total ?? 0),
);
const totalPages = computed(() =>
  Math.max(1, Math.ceil(totalItems.value / PAGE_SIZE)),
);

// Filtre statut client-side (l'API ne filtre pas par isActive)
const stores = computed(() => {
  if (statusFilter.value === 'active')
    return rawStores.value.filter((s) => s.isActive !== false);
  if (statusFilter.value === 'inactive')
    return rawStores.value.filter((s) => s.isActive === false);
  return rawStores.value;
});

// ── Query organisations (pour le filtre) ─────────────────────────────────────
const { data: orgsData } = useQuery({
  queryKey: ['orgs-for-filter'],
  queryFn: async () => {
    const first = await ManageOrganisationService.searchOrganisations(
      1,
      [],
      'name',
      'ASC',
    );
    const meta = first.data as any;
    const total = meta?.total ?? 0;
    const ps = meta?.pageSize ?? 20;
    const extract = (r: any): IManageOrganisation[] => {
      const d = r?.data;
      return Array.isArray(d) ? d : (d?.data ?? []);
    };
    let list = extract(first);
    if (total > ps) {
      const pages = Array.from(
        { length: Math.ceil(total / ps) - 1 },
        (_, i) => i + 2,
      );
      const rest = await Promise.all(
        pages.map((p) =>
          ManageOrganisationService.searchOrganisations(
            p,
            [],
            'name',
            'ASC',
          ).then(extract),
        ),
      );
      list = [...list, ...rest.flat()];
    }
    return list;
  },
  staleTime: 120_000,
});
const allOrgs = computed<IManageOrganisation[]>(() => orgsData.value ?? []);
const filteredOrgs = computed(() => {
  const q = orgSearch.value.toLowerCase();
  return q
    ? allOrgs.value.filter((o) => o.name.toLowerCase().includes(q))
    : allOrgs.value;
});
function selectedOrgName() {
  if (!orgIdFilter.value) return '';
  return allOrgs.value.find((o) => o.id === orgIdFilter.value)?.name ?? '';
}

// ── Query commerces de l'organisation sélectionnée (pour le filtre store) ────
const { data: orgStoresData } = useQuery({
  queryKey: computed(() => ['stores-for-filter', orgIdFilter.value]),
  queryFn: async () => {
    const r = await axiosInstance.get<IApiResponseRestaurant>(
      `restaurants/search?pagination=true&page=1&pageSize=500&organizationId=${orgIdFilter.value}`,
    );
    return r.data?.data ?? [];
  },
  enabled: computed(() => !!orgIdFilter.value),
  staleTime: 30_000,
});
const orgStores = computed<IRestaurant[]>(() => orgStoresData.value ?? []);
const filteredOrgStores = computed(() => {
  const q = storeSearch.value.toLowerCase();
  return q
    ? orgStores.value.filter((s) => s.designation.toLowerCase().includes(q))
    : orgStores.value;
});
function selectedStoreName() {
  if (!storeIdFilter.value) return '';
  return (
    orgStores.value.find((s) => s.id === storeIdFilter.value)?.designation ?? ''
  );
}

// ── Tri colonnes ──────────────────────────────────────────────────────────────
function setSort(col: ApiSortBy) {
  if (sortBy.value === col)
    order.value = order.value === 'ASC' ? 'DESC' : 'ASC';
  else {
    sortBy.value = col;
    order.value = 'ASC';
  }
}

// ── Pagination ───────────────────────────────────────────────────────────────
const pages = computed(() => {
  const p = page.value,
    total = totalPages.value;
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const arr: (number | '…')[] = [1];
  if (p > 3) arr.push('…');
  for (let i = Math.max(2, p - 1); i <= Math.min(total - 1, p + 1); i++)
    arr.push(i);
  if (p < total - 2) arr.push('…');
  arr.push(total);
  return arr;
});

// ── Vue ───────────────────────────────────────────────────────────────────────
const viewMode = ref<ViewMode>('card');

// ── Sélection ─────────────────────────────────────────────────────────────────
const selected = ref<Set<string>>(new Set());
const allSelected = computed(
  () =>
    stores.value.length > 0 &&
    stores.value.every((s) => selected.value.has(s.id)),
);
const someSelected = computed(() => selected.value.size > 0);
function toggleAll() {
  if (allSelected.value) selected.value = new Set();
  else selected.value = new Set(stores.value.map((s) => s.id));
}
function toggleRow(id: string) {
  const s = new Set(selected.value);
  s.has(id) ? s.delete(id) : s.add(id);
  selected.value = s;
}
watch(page, () => {
  selected.value = new Set();
});

// ── Redimensionnement colonnes ────────────────────────────────────────────────
const colWidths = ref<Record<string, number>>({
  logo: 56,
  designation: 240,
  orderCode: 120,
  currency: 100,
  status: 130,
  actions: 56,
});
let resizing: { col: string; startX: number; startW: number } | null = null;
function startResize(col: string, e: MouseEvent) {
  resizing = { col, startX: e.clientX, startW: colWidths.value[col] };
  document.addEventListener('mousemove', onResize);
  document.addEventListener('mouseup', stopResize);
}
function onResize(e: MouseEvent) {
  if (!resizing) return;
  colWidths.value[resizing.col] = Math.max(
    60,
    resizing.startW + e.clientX - resizing.startX,
  );
}
function stopResize() {
  resizing = null;
  document.removeEventListener('mousemove', onResize);
  document.removeEventListener('mouseup', stopResize);
}

// ── Export CSV ────────────────────────────────────────────────────────────────
function exportCSV() {
  const list = stores.value.filter(
    (s) => selected.value.size === 0 || selected.value.has(s.id),
  );
  const rows = [
    [
      t('commerces.csvDesignation'),
      t('commerces.csvCode'),
      t('commerces.csvCurrency'),
      t('commerces.csvStatus'),
      t('commerces.csvAddress'),
    ],
    ...list.map((s) => [
      s.designation,
      s.orderCode ?? '',
      s.currencyCode ?? '',
      s.isActive !== false
        ? t('commerces.csvStatusActive')
        : t('commerces.csvStatusInactive'),
      s.address ?? '',
    ]),
  ];
  const csv = rows
    .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'commerces.csv';
  a.click();
  URL.revokeObjectURL(url);
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function initials(name: string) {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}
function primarySlug(s: IRestaurant) {
  return (
    s.slugs?.find((sl) => sl.isPrimary)?.slug ?? s.slugs?.[0]?.slug ?? null
  );
}
const AVATAR_COLORS = [
  ['#fff7ee', '#f68402'],
  ['#eef3ff', '#3b5bdb'],
  ['#f0fff4', '#2f9e44'],
  ['#fff0f6', '#c2255c'],
  ['#f8f0fe', '#7048e8'],
  ['#e6fcf5', '#0c8599'],
  ['#fff9db', '#e67700'],
  ['#fff5f5', '#c92a2a'],
];
function avatarStyle(name: string) {
  const i = name.charCodeAt(0) % AVATAR_COLORS.length;
  return { background: AVATAR_COLORS[i][0], color: AVATAR_COLORS[i][1] };
}

// ── Chargement des logos (skeleton pendant le fetch, fallback si erreur) ──────
function logoUrl(store: IRestaurant) {
  return store.logo?.info?.secureUrl ?? (store as any).logoUrl ?? null;
}
const logoLoaded = ref<Set<string>>(new Set());
const logoFailed = ref<Set<string>>(new Set());
function onLogoLoad(storeId: string) {
  logoLoaded.value.add(storeId);
  logoLoaded.value = new Set(logoLoaded.value);
}
function onLogoError(storeId: string) {
  logoFailed.value.add(storeId);
  logoFailed.value = new Set(logoFailed.value);
}
function showLogoImg(store: IRestaurant) {
  return !!logoUrl(store) && !logoFailed.value.has(store.id);
}
function logoPending(store: IRestaurant) {
  return showLogoImg(store) && !logoLoaded.value.has(store.id);
}

function clearFilters() {
  search.value = '';
  designationQ.value = '';
  orgIdFilter.value = '';
  unaffiliated.value = false;
  statusFilter.value = 'active';
}
const hasActiveFilters = computed(
  () => !!designationQ.value || !!orgIdFilter.value || unaffiliated.value,
);
</script>

<template>
  <div class="cmp" @click="orgDropOpen = false">
    <!-- ── En-tête ─────────────────────────────────────────────────────────── -->
    <div class="cmp__header">
      <div>
        <h1 class="cmp__title">{{ t('commerces.title') }}</h1>
        <p class="cmp__sub">
          {{ totalItems }}
          {{ t('commerces.sub', { s: totalItems > 1 ? 's' : '' }) }}
        </p>
      </div>
      <div class="cmp__header-right">
        <button class="cmp__btn cmp__btn--ghost" @click="exportCSV">
          <i class="pi pi-download" /> {{ t('commerces.export') }}
        </button>
        <button
          class="cmp__btn cmp__btn--primary"
          @click="router.push('/commerces/new')"
        >
          <i class="pi pi-plus" /> {{ t('commerces.new') }}
        </button>
      </div>
    </div>

    <!-- ── Barre d'outils ──────────────────────────────────────────────────── -->
    <div class="cmp__toolbar">
      <!-- Statut -->
      <div class="cmp__status-tabs">
        <button
          v-for="opt in STATUS_OPTS"
          :key="opt.k"
          class="cmp__status-tab"
          :class="{ 'cmp__status-tab--active': statusFilter === opt.k }"
          @click.stop="statusFilter = opt.k"
        >
          {{ opt.l }}
        </button>
      </div>

      <!-- Recherche désignation -->
      <div class="cmp__search-wrap">
        <i class="pi pi-search cmp__search-icon" />
        <input
          v-model="search"
          class="cmp__search"
          type="text"
          :placeholder="t('commerces.searchPlaceholder')"
          @click.stop
        />
        <button v-if="search" class="cmp__search-clear" @click="search = ''">
          <i class="pi pi-times" />
        </button>
      </div>

      <div class="cmp__toolbar-right">
        <!-- Tri sortBy -->
        <div class="cmp__sort-group">
          <span class="cmp__sort-label">{{ t('commerces.sortLabel') }}</span>
          <button
            class="cmp__sort-btn"
            :class="{ 'cmp__sort-btn--active': sortBy === 'name' }"
            @click="setSort('name')"
          >
            {{ t('commerces.sortName') }}
            <i
              class="pi"
              :class="
                sortBy === 'name'
                  ? order === 'ASC'
                    ? 'pi-sort-up-fill'
                    : 'pi-sort-down-fill'
                  : 'pi-sort'
              "
            />
          </button>
          <button
            class="cmp__sort-btn"
            :class="{ 'cmp__sort-btn--active': sortBy === 'created' }"
            @click="setSort('created')"
          >
            {{ t('commerces.sortDate') }}
            <i
              class="pi"
              :class="
                sortBy === 'created'
                  ? order === 'ASC'
                    ? 'pi-sort-up-fill'
                    : 'pi-sort-down-fill'
                  : 'pi-sort'
              "
            />
          </button>
        </div>

        <!-- Vue -->
        <div class="cmp__view-toggle">
          <button
            :class="[
              'cmp__view-btn',
              viewMode === 'table' && 'cmp__view-btn--active',
            ]"
            @click="viewMode = 'table'"
            :title="t('commerces.viewTable')"
          >
            <i class="pi pi-list" />
          </button>
          <button
            :class="[
              'cmp__view-btn',
              viewMode === 'card' && 'cmp__view-btn--active',
            ]"
            @click="viewMode = 'card'"
            :title="t('commerces.viewCards')"
          >
            <i class="pi pi-th-large" />
          </button>
        </div>
      </div>
    </div>

    <!-- ── Filtres avancés ─────────────────────────────────────────────────── -->
    <div class="cmp__filters" @click.stop>
      <!-- Organisation -->
      <div class="cmp__filter-block">
        <label class="cmp__filter-label"
          ><i class="pi pi-building" /> {{ t('commerces.filterOrg') }}</label
        >
        <div class="cmp__dd" @click.stop>
          <button
            class="cmp__dd-trigger"
            :class="{
              'cmp__dd-trigger--active': !!orgIdFilter,
              'cmp__dd-trigger--open': orgDropOpen,
            }"
            @click="orgDropOpen = !orgDropOpen"
          >
            <span :class="orgIdFilter ? '' : 'cmp__dd-placeholder'">{{
              orgIdFilter ? selectedOrgName() : t('commerces.allOrgs')
            }}</span>
            <i
              class="pi pi-chevron-down cmp__dd-arrow"
              :class="{ 'cmp__dd-arrow--open': orgDropOpen }"
            />
          </button>
          <div v-if="orgDropOpen" class="cmp__dd-panel">
            <div class="cmp__dd-search-wrap">
              <i class="pi pi-search cmp__dd-search-icon" /><input
                v-model="orgSearch"
                class="cmp__dd-search"
                :placeholder="t('commerces.orgSearch')"
                @click.stop
                autofocus
              />
            </div>
            <ul class="cmp__dd-list">
              <li
                class="cmp__dd-item"
                :class="{ 'cmp__dd-item--active': !orgIdFilter }"
                @click="
                  orgIdFilter = '';
                  orgDropOpen = false;
                  orgSearch = '';
                "
              >
                {{ t('commerces.allOrgs') }}
              </li>
              <li
                v-for="o in filteredOrgs"
                :key="o.id"
                class="cmp__dd-item"
                :class="{ 'cmp__dd-item--active': orgIdFilter === o.id }"
                @click="
                  orgIdFilter = o.id;
                  unaffiliated = false;
                  orgDropOpen = false;
                  orgSearch = '';
                "
              >
                {{ o.name }}
              </li>
              <li v-if="filteredOrgs.length === 0" class="cmp__dd-empty">
                {{ t('commerces.noOrgResult') }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Commerce (cascade, actif seulement si une organisation est choisie) -->
      <div v-if="orgIdFilter" class="cmp__filter-block">
        <label class="cmp__filter-label"
          ><i class="pi pi-shop" /> {{ t('commerces.filterStore') }}</label
        >
        <div class="cmp__dd" @click.stop>
          <button
            class="cmp__dd-trigger"
            :class="{
              'cmp__dd-trigger--active': !!storeIdFilter,
              'cmp__dd-trigger--open': storeDropOpen,
            }"
            @click="storeDropOpen = !storeDropOpen"
          >
            <span :class="storeIdFilter ? '' : 'cmp__dd-placeholder'">{{
              storeIdFilter ? selectedStoreName() : t('commerces.allStores')
            }}</span>
            <i
              class="pi pi-chevron-down cmp__dd-arrow"
              :class="{ 'cmp__dd-arrow--open': storeDropOpen }"
            />
          </button>
          <div v-if="storeDropOpen" class="cmp__dd-panel">
            <div class="cmp__dd-search-wrap">
              <i class="pi pi-search cmp__dd-search-icon" /><input
                v-model="storeSearch"
                class="cmp__dd-search"
                :placeholder="t('commerces.storeSearch')"
                @click.stop
                autofocus
              />
            </div>
            <ul class="cmp__dd-list">
              <li
                class="cmp__dd-item"
                :class="{ 'cmp__dd-item--active': !storeIdFilter }"
                @click="
                  storeIdFilter = '';
                  storeDropOpen = false;
                  storeSearch = '';
                "
              >
                {{ t('commerces.allStores') }}
              </li>
              <li
                v-for="s in filteredOrgStores"
                :key="s.id"
                class="cmp__dd-item"
                :class="{ 'cmp__dd-item--active': storeIdFilter === s.id }"
                @click="
                  storeIdFilter = s.id;
                  storeDropOpen = false;
                  storeSearch = '';
                "
              >
                {{ s.designation }}
              </li>
              <li v-if="filteredOrgStores.length === 0" class="cmp__dd-empty">
                {{ t('commerces.noStoreResult') }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Non affiliés -->
      <div class="cmp__filter-block">
        <label class="cmp__filter-label"
          ><i class="pi pi-link" />
          {{ t('commerces.filterUnaffiliated') }}</label
        >
        <label class="cmp__toggle" @click.stop>
          <input
            type="checkbox"
            v-model="unaffiliated"
            class="cmp__toggle-input"
            @change="unaffiliated ? (orgIdFilter = '') : null"
          />
          <span class="cmp__toggle-track"
            ><span class="cmp__toggle-thumb"
          /></span>
          <span class="cmp__toggle-text">{{
            unaffiliated ? t('commerces.yes') : t('commerces.no')
          }}</span>
        </label>
      </div>

      <!-- Effacer filtres -->
      <button
        v-if="hasActiveFilters"
        class="cmp__clear-btn"
        @click="clearFilters"
      >
        <i class="pi pi-times" /> {{ t('commerces.resetFilters') }}
      </button>

      <!-- Compteur -->
      <div class="cmp__count-info">
        <span class="cmp__count"
          >{{ stores.length }}
          {{
            t('commerces.results', { s: stores.length > 1 ? 's' : '' })
          }}</span
        >
        <span v-if="someSelected" class="cmp__selected-badge"
          >{{ selected.size }}
          {{
            t('commerces.selected', { s: selected.size > 1 ? 's' : '' })
          }}</span
        >
      </div>
    </div>

    <!-- ── Skeleton ─────────────────────────────────────────────────────────── -->
    <div v-if="isPending" class="cmp__skeletons">
      <div v-for="i in 6" :key="i" class="cmp__skeleton" />
    </div>

    <!-- ── Erreur ────────────────────────────────────────────────────────────── -->
    <div v-else-if="isError" class="cmp__empty">
      <i
        class="pi pi-exclamation-circle cmp__empty-icon cmp__empty-icon--error"
      />
      <p class="cmp__empty-title">{{ t('commerces.loadError') }}</p>
    </div>

    <!-- ── Aucun résultat ────────────────────────────────────────────────────── -->
    <div v-else-if="stores.length === 0" class="cmp__empty">
      <i class="pi pi-shop cmp__empty-icon" />
      <p class="cmp__empty-title">{{ t('commerces.empty') }}</p>
      <button class="cmp__btn cmp__btn--ghost" @click="clearFilters">
        {{ t('commerces.resetFiltersBtn') }}
      </button>
    </div>

    <template v-else>
      <!-- ════ VUE TABLEAU ════ -->
      <div v-if="viewMode === 'table'" class="cmp__table-container">
        <div class="cmp__table-scroll">
          <table class="cmp__table">
            <thead>
              <tr>
                <th class="cmp__th cmp__th--check">
                  <label class="cmp__checkbox"
                    ><input
                      type="checkbox"
                      :checked="allSelected"
                      :indeterminate="someSelected && !allSelected"
                      @change="toggleAll" /><span class="cmp__checkbox-box"
                  /></label>
                </th>
                <th
                  class="cmp__th"
                  :style="{
                    width: colWidths.logo + 'px',
                    minWidth: colWidths.logo + 'px',
                  }"
                >
                  <div class="cmp__th-inner">{{ t('commerces.colLogo') }}</div>
                  <div
                    class="cmp__th-resize"
                    @mousedown.stop="startResize('logo', $event)"
                  />
                </th>
                <th
                  class="cmp__th"
                  :style="{
                    width: colWidths.designation + 'px',
                    minWidth: colWidths.designation + 'px',
                  }"
                >
                  <div
                    class="cmp__th-inner cmp__th-inner--sort"
                    @click="setSort('name')"
                  >
                    {{ t('commerces.colDesignation') }}
                    <i
                      class="pi cmp__sort-icon"
                      :class="
                        sortBy === 'name'
                          ? order === 'ASC'
                            ? 'pi-sort-up-fill'
                            : 'pi-sort-down-fill'
                          : 'pi-sort'
                      "
                    />
                  </div>
                  <div
                    class="cmp__th-resize"
                    @mousedown.stop="startResize('designation', $event)"
                  />
                </th>
                <th
                  class="cmp__th"
                  :style="{
                    width: colWidths.orderCode + 'px',
                    minWidth: colWidths.orderCode + 'px',
                  }"
                >
                  <div class="cmp__th-inner">{{ t('commerces.colCode') }}</div>
                  <div
                    class="cmp__th-resize"
                    @mousedown.stop="startResize('orderCode', $event)"
                  />
                </th>
                <th
                  class="cmp__th"
                  :style="{
                    width: colWidths.currency + 'px',
                    minWidth: colWidths.currency + 'px',
                  }"
                >
                  <div class="cmp__th-inner">
                    {{ t('commerces.colCurrency') }}
                  </div>
                  <div
                    class="cmp__th-resize"
                    @mousedown.stop="startResize('currency', $event)"
                  />
                </th>
                <th
                  class="cmp__th"
                  :style="{
                    width: colWidths.status + 'px',
                    minWidth: colWidths.status + 'px',
                  }"
                >
                  <div class="cmp__th-inner">
                    {{ t('commerces.colStatus') }}
                  </div>
                  <div
                    class="cmp__th-resize"
                    @mousedown.stop="startResize('status', $event)"
                  />
                </th>
                <th
                  class="cmp__th cmp__th--actions"
                  :style="{
                    width: colWidths.actions + 'px',
                    minWidth: colWidths.actions + 'px',
                  }"
                />
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="store in stores"
                :key="store.id"
                class="cmp__tr"
                :class="{
                  'cmp__tr--selected': selected.has(store.id),
                  'cmp__tr--inactive': store.isActive === false,
                }"
                @click="openStore(store.id)"
              >
                <td class="cmp__td cmp__td--check" @click.stop>
                  <label class="cmp__checkbox"
                    ><input
                      type="checkbox"
                      :checked="selected.has(store.id)"
                      @change="toggleRow(store.id)" /><span
                      class="cmp__checkbox-box"
                  /></label>
                </td>
                <td class="cmp__td">
                  <div class="cmp__logo-cell">
                    <div v-if="logoPending(store)" class="cmp__logo-skeleton" />
                    <img
                      v-if="showLogoImg(store)"
                      v-show="logoLoaded.has(store.id)"
                      :src="logoUrl(store)!"
                      class="cmp__logo-img"
                      alt=""
                      @load="onLogoLoad(store.id)"
                      @error="onLogoError(store.id)"
                    />
                    <div
                      v-else-if="!logoPending(store)"
                      class="cmp__avatar"
                      :style="avatarStyle(store.designation)"
                    >
                      {{ initials(store.designation) }}
                    </div>
                  </div>
                </td>
                <td class="cmp__td">
                  <div class="cmp__name">{{ store.designation }}</div>
                  <div v-if="primarySlug(store)" class="cmp__slug">
                    {{ primarySlug(store) }}
                  </div>
                </td>
                <td class="cmp__td">
                  <span class="cmp__code-badge">{{
                    store.orderCode ?? '—'
                  }}</span>
                </td>
                <td class="cmp__td">
                  <span class="cmp__currency">{{
                    store.currencyCode ?? '—'
                  }}</span>
                </td>
                <td class="cmp__td">
                  <span
                    class="cmp__status"
                    :class="
                      store.isActive !== false
                        ? 'cmp__status--active'
                        : 'cmp__status--inactive'
                    "
                  >
                    <span class="cmp__status-dot" />
                    {{
                      store.isActive !== false
                        ? t('commerces.statusActive')
                        : t('commerces.statusInactive')
                    }}
                  </span>
                </td>
                <td class="cmp__td cmp__td--actions" @click.stop>
                  <button
                    class="cmp__action-btn"
                    :title="t('commerces.editTitle')"
                    @click="router.push(`/commerces/${store.id}/edit`)"
                  >
                    <i class="pi pi-pencil" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ════ VUE CARTE ════ -->
      <div v-else class="cmp__cards-wrap">
        <div class="cmp__cards">
          <div
            v-for="store in stores"
            :key="store.id"
            class="cmp__card group"
            :class="{ 'cmp__card--inactive': store.isActive === false }"
            @click="openStore(store.id)"
          >
            <!-- Top : avatar + badge statut -->
            <div class="cmp__card-head">
              <div class="cmp__card-avatar-wrap">
                <div
                  v-if="logoPending(store)"
                  class="cmp__card-logo-skeleton"
                />
                <img
                  v-if="showLogoImg(store)"
                  v-show="logoLoaded.has(store.id)"
                  :src="logoUrl(store)!"
                  class="cmp__card-logo-img"
                  alt=""
                  @load="onLogoLoad(store.id)"
                  @error="onLogoError(store.id)"
                />
                <div
                  v-else-if="!logoPending(store)"
                  class="cmp__card-avatar"
                  :style="avatarStyle(store.designation)"
                >
                  {{ initials(store.designation) }}
                </div>
              </div>
              <span
                class="cmp__status"
                :class="
                  store.isActive !== false
                    ? 'cmp__status--active'
                    : 'cmp__status--inactive'
                "
              >
                <span class="cmp__status-dot" />{{
                  store.isActive !== false
                    ? t('commerces.statusActive')
                    : t('commerces.statusInactive')
                }}
              </span>
            </div>

            <!-- Nom + slug -->
            <div class="cmp__card-mid">
              <span class="cmp__card-id-label">{{
                t('commerces.cardLabel')
              }}</span>
              <h3 class="cmp__card-name">{{ store.designation }}</h3>
              <p v-if="primarySlug(store)" class="cmp__card-slug">
                {{ primarySlug(store) }}
              </p>
            </div>

            <!-- Méta : code + devise + adresse -->
            <div class="cmp__card-info">
              <div v-if="store.orderCode" class="cmp__card-info-row">
                <span class="cmp__card-info-label">{{
                  t('commerces.cardCode')
                }}</span>
                <span class="cmp__card-info-val cmp__card-info-val--mono">{{
                  store.orderCode
                }}</span>
              </div>
              <div v-if="store.currencyCode" class="cmp__card-info-row">
                <span class="cmp__card-info-label">{{
                  t('commerces.cardCurrency')
                }}</span>
                <span class="cmp__card-info-val">{{ store.currencyCode }}</span>
              </div>
              <div v-if="store.address" class="cmp__card-info-row">
                <span class="cmp__card-info-label">{{
                  t('commerces.cardAddress')
                }}</span>
                <span class="cmp__card-info-val cmp__card-info-val--trunc">{{
                  store.address
                }}</span>
              </div>
            </div>

            <!-- Actions hover -->
            <div class="cmp__card-actions" @click.stop>
              <button class="cmp__card-btn" @click="openStore(store.id)">
                {{ t('commerces.details') }}
              </button>
              <button
                class="cmp__card-btn cmp__card-btn--primary"
                @click="router.push(`/commerces/${store.id}/edit`)"
              >
                {{ t('commerces.edit') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Pagination ──────────────────────────────────────────────────────── -->
      <div class="cmp__pagination">
        <span class="cmp__pagination-info">
          {{
            t('commerces.page', {
              page,
              total: totalPages,
              n: totalItems,
              s: totalItems > 1 ? 's' : '',
            })
          }}
        </span>
        <div class="cmp__pagination-btns">
          <button
            class="cmp__page-btn"
            :disabled="page === 1"
            @click="page = 1"
          >
            <i class="pi pi-angle-double-left" />
          </button>
          <button class="cmp__page-btn" :disabled="page === 1" @click="page--">
            <i class="pi pi-chevron-left" />
          </button>
          <template v-for="p in pages" :key="p">
            <button
              v-if="p !== '…'"
              class="cmp__page-btn"
              :class="{ 'cmp__page-btn--active': page === p }"
              @click="page = p as number"
            >
              {{ p }}
            </button>
            <span v-else class="cmp__page-ellipsis">…</span>
          </template>
          <button
            class="cmp__page-btn"
            :disabled="page === totalPages"
            @click="page++"
          >
            <i class="pi pi-chevron-right" />
          </button>
          <button
            class="cmp__page-btn"
            :disabled="page === totalPages"
            @click="page = totalPages"
          >
            <i class="pi pi-angle-double-right" />
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.cmp {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-width: 0;
  padding: 2.5rem 3rem;
  box-sizing: border-box;
}
@media (max-width: 1023px) {
  .cmp {
    padding: 1.5rem;
  }
}
@media (max-width: 639px) {
  .cmp {
    padding: 1rem;
    gap: 1rem;
  }
}

/* En-tête */
.cmp__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}
.cmp__title {
  margin: 0 0 0.15rem;
  font-size: 1.5rem;
  font-weight: 900;
  letter-spacing: -0.03em;
  color: var(--nxt-on-surface);
}
.cmp__sub {
  margin: 0;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted);
}
.cmp__header-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

/* Boutons */
.cmp__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 2.375rem;
  padding: 0 1rem;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  border: none;
  transition:
    filter 0.15s,
    background 0.15s;
}
.cmp__btn--primary {
  background: #ffd60a;
  color: #0b1320;
  box-shadow: 0 2px 8px rgba(255, 214, 10, 0.35);
}
.cmp__btn--primary:hover {
  background: #e6c109;
  box-shadow: 0 6px 16px rgba(255, 214, 10, 0.4);
  transform: translateY(-1px);
}
.cmp__btn--ghost {
  background: #fff;
  border: 1px solid rgba(11, 19, 32, 0.12);
  color: rgba(11, 19, 32, 0.65);
}
.cmp__btn--ghost:hover {
  background: rgba(11, 19, 32, 0.04);
  color: #0b1320;
  border-color: rgba(11, 19, 32, 0.2);
}

/* Toolbar */
.cmp__toolbar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1rem;
  padding: 0.75rem 1rem;
  box-shadow: var(--nxt-shadow-sm);
}
.cmp__status-tabs {
  display: flex;
  gap: 0.25rem;
}
.cmp__status-tab {
  height: 2rem;
  padding: 0 0.875rem;
  border: 1px solid transparent;
  border-radius: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  background: none;
  color: var(--nxt-on-surface-muted);
  transition: all 0.15s;
}
.cmp__status-tab:hover {
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface);
}
.cmp__status-tab--active {
  background: #0b1320;
  color: #fff;
  border-color: #0b1320;
  font-weight: 700;
}

.cmp__search-wrap {
  flex: 1;
  min-width: 180px;
  max-width: 320px;
  position: relative;
}
.cmp__search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
  pointer-events: none;
}
.cmp__search {
  width: 100%;
  height: 2.25rem;
  padding: 0 2rem 0 2.25rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s;
}
.cmp__search:focus {
  border-color: #ffd60a;
  box-shadow: 0 0 0 3px rgba(255, 214, 10, 0.2);
}
.cmp__search::placeholder {
  color: var(--nxt-on-surface-muted);
}
.cmp__search-clear {
  position: absolute;
  right: 0.625rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: var(--nxt-on-surface-muted);
  font-size: 0.75rem;
  padding: 0;
}

.cmp__toolbar-right {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  margin-left: auto;
}

/* Tri */
.cmp__sort-group {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}
.cmp__sort-label {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
  white-space: nowrap;
}
.cmp__sort-btn {
  height: 2rem;
  padding: 0 0.75rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-muted);
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  transition: all 0.15s;
}
.cmp__sort-btn:hover {
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface);
}
.cmp__sort-btn--active {
  background: var(--nxt-surface-mid);
  color: var(--nxt-primary);
  border-color: var(--nxt-primary);
  font-weight: 700;
}
.cmp__sort-btn .pi {
  font-size: 0.6875rem;
}

/* Vue toggle */
.cmp__view-toggle {
  display: flex;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.5rem;
  overflow: hidden;
}
.cmp__view-btn {
  width: 2rem;
  height: 2rem;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted);
  transition:
    background 0.15s,
    color 0.15s;
}
.cmp__view-btn:hover {
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface);
}
.cmp__view-btn--active {
  background: #0b1320;
  color: #fff;
}

/* Filtres avancés */
.cmp__filters {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1rem;
  padding: 0.875rem 1rem;
  box-shadow: var(--nxt-shadow-sm);
}
.cmp__filter-block {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}
.cmp__filter-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--nxt-on-surface-muted);
  display: flex;
  align-items: center;
  gap: 0.35rem;
  white-space: nowrap;
}
.cmp__filter-label .pi {
  font-size: 0.75rem;
}

/* Dropdown org */
.cmp__dd {
  position: relative;
}
.cmp__dd-trigger {
  height: 2.25rem;
  padding: 0 0.875rem;
  min-width: 200px;
  max-width: 280px;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  outline: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.cmp__dd-trigger:hover,
.cmp__dd-trigger--open {
  border-color: var(--nxt-primary);
}
.cmp__dd-trigger--active {
  color: var(--nxt-primary);
  border-color: var(--nxt-primary);
  background: rgba(0, 0, 0, 0.04);
  font-weight: 700;
  font-size: 0.75rem;
}
.cmp__dd-placeholder {
  color: var(--nxt-on-surface-muted);
  font-weight: 400;
}
.cmp__dd-arrow {
  font-size: 0.6875rem;
  color: var(--nxt-on-surface-muted);
  transition: transform 0.2s;
  flex-shrink: 0;
}
.cmp__dd-arrow--open {
  transform: rotate(180deg);
}
.cmp__dd-panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 200;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.75rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  min-width: 240px;
}
.cmp__dd-search-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  border-bottom: 1px solid var(--nxt-outline-variant);
}
.cmp__dd-search-icon {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
  flex-shrink: 0;
}
.cmp__dd-search {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.875rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
}
.cmp__dd-search::placeholder {
  color: var(--nxt-on-surface-muted);
}
.cmp__dd-list {
  list-style: none;
  margin: 0;
  padding: 0.375rem;
  max-height: 220px;
  overflow-y: auto;
}
.cmp__dd-item {
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: var(--nxt-on-surface);
  transition: background 0.1s;
}
.cmp__dd-item:hover {
  background: var(--nxt-surface-mid);
}
.cmp__dd-item--active {
  background: var(--nxt-surface-mid);
  font-weight: 700;
  color: var(--nxt-primary);
}
.cmp__dd-empty {
  padding: 0.75rem;
  text-align: center;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}

/* Toggle non affiliés */
.cmp__toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}
.cmp__toggle-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}
.cmp__toggle-track {
  width: 2.25rem;
  height: 1.25rem;
  border-radius: 9999px;
  background: var(--nxt-outline-variant);
  position: relative;
  transition: background 0.2s;
  flex-shrink: 0;
}
.cmp__toggle-input:checked + .cmp__toggle-track {
  background: var(--nxt-primary);
}
.cmp__toggle-thumb {
  position: absolute;
  top: 0.1875rem;
  left: 0.1875rem;
  width: 0.875rem;
  height: 0.875rem;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}
.cmp__toggle-input:checked + .cmp__toggle-track .cmp__toggle-thumb {
  transform: translateX(1rem);
}
.cmp__toggle-text {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--nxt-on-surface);
  min-width: 2rem;
}

.cmp__clear-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  height: 2rem;
  padding: 0 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid var(--nxt-error, #e53935);
  background: rgba(229, 57, 53, 0.06);
  color: var(--nxt-error, #e53935);
  font-size: 0.8125rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;
}
.cmp__clear-btn:hover {
  background: rgba(229, 57, 53, 0.12);
}

.cmp__count-info {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.625rem;
}
.cmp__count {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
  white-space: nowrap;
}
.cmp__selected-badge {
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.2rem 0.625rem;
  border-radius: 9999px;
  background: rgba(0, 0, 0, 0.1);
  color: var(--nxt-primary);
  border: 1px solid rgba(0, 0, 0, 0.2);
}

/* Skeleton */
.cmp__skeletons {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.cmp__skeleton {
  height: 3.5rem;
  border-radius: 0.75rem;
  background: linear-gradient(
    90deg,
    var(--nxt-surface-low) 25%,
    var(--nxt-surface-mid) 50%,
    var(--nxt-surface-low) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}
@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Empty */
.cmp__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.875rem;
  padding: 4rem 2rem;
  text-align: center;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1.25rem;
}
.cmp__empty-icon {
  font-size: 2.25rem;
  color: var(--nxt-on-surface-muted);
  opacity: 0.3;
}
.cmp__empty-icon--error {
  color: var(--nxt-error);
  opacity: 1;
}
.cmp__empty-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}

/* Tableau */
.cmp__table-container {
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1.25rem;
  box-shadow: var(--nxt-shadow-sm);
  overflow: hidden;
}
.cmp__table-scroll {
  overflow-x: auto;
}
.cmp__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}
.cmp__th {
  position: relative;
  padding: 0.625rem 0.875rem;
  background: var(--nxt-surface-low);
  border-bottom: 1px solid var(--nxt-outline-variant);
  color: var(--nxt-on-surface-muted);
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  white-space: nowrap;
  user-select: none;
}
.cmp__th--check {
  width: 3rem;
  min-width: 3rem;
}
.cmp__th--actions {
  width: 3.5rem;
  min-width: 3.5rem;
}
.cmp__th-inner {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}
.cmp__th-inner--sort {
  cursor: pointer;
}
.cmp__th-inner--sort:hover {
  color: var(--nxt-on-surface);
}
.cmp__sort-icon {
  font-size: 0.6875rem;
}
.cmp__th-resize {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  cursor: col-resize;
  background: transparent;
  transition: background 0.15s;
}
.cmp__th-resize:hover {
  background: var(--nxt-primary);
}
.cmp__tr {
  cursor: pointer;
  border-bottom: 1px solid var(--nxt-outline-variant);
  transition: background 0.12s;
}
.cmp__tr:last-child {
  border-bottom: none;
}
.cmp__tr:hover {
  background: var(--nxt-surface-low);
}
.cmp__tr--selected {
  background: rgba(0, 0, 0, 0.04);
}
.cmp__tr--inactive {
  opacity: 0.55;
}
.cmp__td {
  padding: 0.75rem 0.875rem;
  color: var(--nxt-on-surface);
  vertical-align: middle;
}
.cmp__td--check {
  width: 3rem;
}
.cmp__td--actions {
  text-align: center;
}
.cmp__checkbox {
  display: inline-flex;
  cursor: pointer;
}
.cmp__checkbox input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}
.cmp__checkbox-box {
  width: 1rem;
  height: 1rem;
  border-radius: 0.25rem;
  border: 1.5px solid var(--nxt-outline-variant);
  background: var(--nxt-card-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}
.cmp__checkbox input:checked + .cmp__checkbox-box {
  background: var(--nxt-primary);
  border-color: var(--nxt-primary);
}
.cmp__checkbox input:checked + .cmp__checkbox-box::after {
  content: '';
  display: block;
  width: 0.3125rem;
  height: 0.5625rem;
  border: 2px solid #fff;
  border-top: none;
  border-left: none;
  transform: rotate(45deg) translateY(-1px);
}
.cmp__logo-cell {
  position: relative;
  width: 2.25rem;
  height: 2.25rem;
}
.cmp__logo-img {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.5rem;
  object-fit: contain;
  border: 1px solid var(--nxt-outline-variant);
}
.cmp__logo-skeleton {
  position: absolute;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.5rem;
  background: linear-gradient(
    90deg,
    var(--nxt-surface-low) 25%,
    var(--nxt-surface-mid) 50%,
    var(--nxt-surface-low) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}
.cmp__avatar {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 800;
}
.cmp__name {
  font-weight: 700;
  color: var(--nxt-on-surface);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 220px;
}
.cmp__slug {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
}
.cmp__code-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.625rem;
  border-radius: 0.375rem;
  background: var(--nxt-surface-mid);
  border: 1px solid var(--nxt-outline-variant);
  font-size: 0.75rem;
  font-weight: 700;
  font-family: monospace;
  color: var(--nxt-on-surface);
}
.cmp__currency {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
  font-weight: 600;
}
.cmp__status {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.2rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 700;
}
.cmp__status--active {
  background: var(--nxt-status-up-bg);
  color: var(--nxt-status-up);
}
.cmp__status--inactive {
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface-muted);
}
.cmp__status-dot {
  width: 0.4375rem;
  height: 0.4375rem;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
}
.cmp__action-btn {
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-card-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  transition: all 0.15s;
}
.cmp__action-btn:hover {
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface);
  border-color: var(--nxt-outline);
}

/* ── Cartes (style page commandes) ──────────────────────────────────────────── */
.cmp__cards-wrap {
  background: rgba(240, 243, 255, 0.35);
  border-radius: 2rem;
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.cmp__cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
}

.cmp__card {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 2rem;
  padding: 1.75rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  cursor: pointer;
  transition:
    box-shadow 0.5s cubic-bezier(0.2, 1, 0.3, 1),
    transform 0.5s cubic-bezier(0.2, 1, 0.3, 1);
}
.cmp__card:hover {
  box-shadow: 0 20px 48px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}
.cmp__card--inactive {
  opacity: 0.55;
}

/* Head : avatar + statut */
.cmp__card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}
.cmp__card-avatar-wrap {
  position: relative;
  width: 3.5rem;
  height: 3.5rem;
  flex-shrink: 0;
  border-radius: 1rem;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.06);
}
.cmp__card-logo-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.cmp__card-logo-skeleton {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    var(--nxt-surface-low) 25%,
    var(--nxt-surface-mid) 50%,
    var(--nxt-surface-low) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}
.cmp__card-avatar {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.125rem;
  font-weight: 800;
}

/* Mid : nom */
.cmp__card-mid {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}
.cmp__card-id-label {
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #4d4632;
  opacity: 0.55;
}
.cmp__card-name {
  font-size: 1.0625rem;
  font-weight: 700;
  color: #141c29;
  margin: 0.1rem 0 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cmp__card-slug {
  font-size: 0.6875rem;
  color: #4d4632;
  opacity: 0.55;
  margin: 0;
}

/* Info rows */
.cmp__card-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.875rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.35);
  border-bottom: 1px solid rgba(255, 255, 255, 0.35);
}
.cmp__card-info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}
.cmp__card-info-label {
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #4d4632;
  opacity: 0.55;
}
.cmp__card-info-val {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #141c29;
}
.cmp__card-info-val--mono {
  font-family: monospace;
  font-size: 0.75rem;
}
.cmp__card-info-val--trunc {
  max-width: 60%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: right;
}

/* Actions hover */
.cmp__card-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.625rem;
  opacity: 0;
  transition: opacity 0.3s;
}
.cmp__card:hover .cmp__card-actions {
  opacity: 1;
}

.cmp__card-btn {
  padding: 0.75rem;
  border-radius: 0.875rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  border: none;
  transition:
    opacity 0.15s,
    background 0.15s;
  text-align: center;
}
.cmp__card-btn {
  background: #f0f3ff;
  color: #141c29;
  border: 1px solid rgba(255, 255, 255, 0.6);
}
.cmp__card-btn:hover {
  background: #fff;
}
.cmp__card-btn--primary {
  background: #ffd60a;
  color: #0b1320;
  border-color: transparent;
}
.cmp__card-btn--primary:hover {
  background: #e6c109;
}

/* Pagination */
.cmp__pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.875rem 1rem;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1rem;
  flex-wrap: wrap;
}
.cmp__pagination-info {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.cmp__pagination-btns {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}
.cmp__page-btn {
  min-width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-variant);
  font-size: 0.8125rem;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.375rem;
}
.cmp__page-btn:hover:not(:disabled) {
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface);
}
.cmp__page-btn--active {
  background: #0b1320;
  color: #fff;
  border-color: #0b1320;
  font-weight: 700;
}
.cmp__page-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}
.cmp__page-ellipsis {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
  padding: 0 0.25rem;
}
</style>

<style>
[data-theme='dark'] .cmp__cards-wrap {
  background: rgba(20, 22, 30, 0.35);
}
[data-theme='dark'] .cmp__card {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cmp__toolbar,
[data-theme='dark'] .cmp__filters,
[data-theme='dark'] .cmp__pagination {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cmp__status-tab {
  color: rgba(255, 255, 255, 0.45);
}
[data-theme='dark'] .cmp__status-tab:hover {
  color: #fff;
}
[data-theme='dark'] .cmp__sort-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
}
[data-theme='dark'] .cmp__sort-btn--active {
  background: rgba(255, 214, 10, 0.12);
  color: #ffd60a;
  border-color: #ffd60a;
}
[data-theme='dark'] .cmp__dd-trigger {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .cmp__dd-trigger:hover {
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cmp__dd-panel {
  background: #111c2e;
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .cmp__dd-item:hover {
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cmp__dd-item--active {
  background: rgba(255, 214, 10, 0.12);
  color: #ffd60a;
}
[data-theme='dark'] .cmp__toggle-track {
  background: rgba(255, 255, 255, 0.15);
}
[data-theme='dark'] .cmp__toggle-input:checked + .cmp__toggle-track {
  background: #ffd60a;
}
[data-theme='dark'] .cmp__count {
  color: rgba(255, 255, 255, 0.45);
}
[data-theme='dark'] .cmp__page-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
}
[data-theme='dark'] .cmp__page-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .cmp__card:hover {
  border-color: rgba(255, 214, 10, 0.25);
  box-shadow: 0 20px 48px rgba(0, 0, 0, 0.3);
}

/* Dark mode — card textes */
[data-theme='dark'] .cmp__card-id-label,
[data-theme='dark'] .cmp__card-slug,
[data-theme='dark'] .cmp__card-info-label {
  color: rgba(255, 255, 255, 0.35);
  opacity: 1;
}
[data-theme='dark'] .cmp__card-name {
  color: #f1f5f9;
}
[data-theme='dark'] .cmp__card-info-val {
  color: #e2e8f0;
}
[data-theme='dark'] .cmp__card-info {
  border-top-color: rgba(255, 255, 255, 0.06);
  border-bottom-color: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .cmp__card-avatar-wrap {
  border-color: rgba(255, 255, 255, 0.08);
}

/* Dark mode — card boutons hover */
[data-theme='dark'] .cmp__card-btn {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.7);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cmp__card-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .cmp__card-btn--primary {
  background: #ffd60a;
  color: #0b1320;
  border-color: transparent;
}
[data-theme='dark'] .cmp__card-btn--primary:hover {
  background: #ffe033;
  opacity: 1;
}

/* Dark mode — status tabs */
[data-theme='dark'] .cmp__status-tab--active {
  background: #ffd60a;
  color: #0b1320;
  border-color: #ffd60a;
}

/* Dark mode — view toggle */
[data-theme='dark'] .cmp__view-toggle {
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .cmp__view-btn {
  background: transparent;
  color: rgba(255, 255, 255, 0.5);
}
[data-theme='dark'] .cmp__view-btn:hover {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.85);
}
[data-theme='dark'] .cmp__view-btn--active {
  background: #ffd60a;
  color: #0b1320;
}

/* Dark mode — search */
[data-theme='dark'] .cmp__search:focus {
  border-color: #ffd60a;
  box-shadow: 0 0 0 3px rgba(255, 214, 10, 0.15);
}

/* Dark mode — ghost button */
[data-theme='dark'] .cmp__btn--ghost {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
}
[data-theme='dark'] .cmp__btn--ghost:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

/* Dark mode — pagination */
[data-theme='dark'] .cmp__page-btn--active {
  background: #ffd60a;
  color: #0b1320;
  border-color: #ffd60a;
}
</style>
